import React from 'react';
import {WeatherService} from '../api';
import {
  ForecastMapConfig,
  ForecastModel,
  HourlyWeatherForecastItem,
  WeatherForecast,
} from '../api/types';
import {Units} from '../utils';
import useApiClient from './useApiClient';
import {useUserUnits} from './userContext';

/*
Click-to-probe hook for the interactive forecast map view.

Lives in core (no DOM/MapLibre usage) so a future mobile map screen can
reuse it: the webapp passes the clicked map coordinate, the current frame's
valid time and the config's forecast model; the hook answers the value to
show in the popup.
*/

// Hourly-series measures the point popup can display. DD/ICON are excluded
// on purpose — they are not scalar "value + unit" readings.
export type ForecastMapPointMeasure =
  | 'T'
  | 'RR'
  | 'SN'
  | 'FF'
  | 'FXY'
  | 'SLP'
  | 'U';

// Forecast model identity exactly as the map config/manifest exposes it
// (ForecastMapModelSummary.name / mesh_size).
export type ForecastMapPointModel = {
  name: string;
  mesh_size?: string | null;
};

export type ForecastMapPoint = {
  lat: number;
  lon: number;
};

export type ForecastMapPointValueStatus =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'error';

// Never attribute a series item more than 3 h away to a frame: past the
// point-series horizon (e.g. day-10 GFS frames vs a shorter hourly series)
// showing the nearest value would be wildly wrong — show "no data" instead.
const MAX_MATCH_DISTANCE_S = 3 * 3600;

/*
Map a forecast-map config to the popup measure.

The map config identifies its variable with the model's raw metric code
(GRIB-style shortnames like `2t`, `tp`, `msl`, `10fg`), while the point
forecast series uses SmartMixin measure keys (T/RR/FF/…): there is no
shared identifier, so this is a best-effort heuristic over the first
layer's metric code. Vector layers (barbs/vector_field/magnitude) are wind
by construction. Anything unrecognised falls back to temperature — the v1
default and the most common map preset.
*/
const METRIC_MEASURES: [RegExp, ForecastMapPointMeasure][] = [
  // Gusts before generic wind/temperature: '10fg', 'gust', 'fg310'…
  [/gust|fg/, 'FXY'],
  [/^(2t|t2m|2mt|t|tmp|skt)$|temp/, 'T'],
  [/^tp$|^rr|apcp|precip|rain/, 'RR'],
  [/^(sd|sde)$|^sn|snow/, 'SN'],
  [/^(msl|prmsl|mslet|slp|sp)$|pres/, 'SLP'],
  [/^(2r|r2|rh|r)$|hum/, 'U'],
  [/^(10si|si10|ws|ff)$|wind|speed/, 'FF'],
];

export const forecastMapPointMeasure = (
  config: Pick<ForecastMapConfig, 'parameters'> | null | undefined,
): ForecastMapPointMeasure => {
  const layer = config?.parameters?.layers?.[0];
  if (!layer) {
    return 'T';
  }
  if (!('metric' in layer)) {
    // barbs / vector_field / magnitude carry a U/V pair — wind by construction.
    return 'FF';
  }
  const metric = layer.metric?.toLowerCase() ?? '';
  for (const [pattern, measure] of METRIC_MEASURES) {
    if (pattern.test(metric)) {
      return measure;
    }
  }
  return 'T';
};

type PointValueResult = {
  // Which (model, point) key produced this result; a mismatch means a fetch
  // is in flight for the current key.
  key: string | null;
  status: 'ready' | 'error';
  forecast: WeatherForecast | null;
};

/**
 * Fetches the point forecast for a clicked map coordinate and selects the
 * value matching the current frame's valid time.
 *
 * A new non-null point (or model) triggers a SINGLE
 * WeatherService.locationWeatherForecast fetch; the response carries the
 * full hourly series, so scrubbing frames while the popup is open
 * re-selects from the already-fetched series without refetching.
 *
 * Frame matching: exact timestamp first, else the nearest hourly item
 * within 3 h (GFS frames are 3-hourly past the hourly horizon).
 */
const useForecastMapPointValue = (
  model: ForecastMapPointModel | null,
  point: ForecastMapPoint | null,
  frameTime: string,
  measure: ForecastMapPointMeasure = 'T',
) => {
  const {fetch} = useApiClient();
  const units = useUserUnits();
  const [result, setResult] = React.useState<PointValueResult>({
    key: null,
    status: 'ready',
    forecast: null,
  });

  const modelName = model?.name;
  const meshSize = model?.mesh_size ?? null;
  const lat = point?.lat;
  const lon = point?.lon;
  const key =
    modelName === undefined || lat === undefined || lon === undefined
      ? null
      : `${modelName}:${meshSize ?? ''}:${lat}:${lon}`;

  // The units header is read when the request goes out rather than tracked as
  // a dependency: `useUserUnits` may hand out a fresh object per render, and
  // the popup contract is one fetch per point.
  const unitsHeader = React.useEffectEvent(() => JSON.stringify(units));

  React.useEffect(() => {
    if (key === null || lat === undefined || lon === undefined) {
      return;
    }
    let cancelled = false;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC';
    fetch(
      WeatherService.locationWeatherForecast({
        lat,
        lon,
        timezone,
        forecastModel: modelName as ForecastModel,
        meshSize,
      }),
      // Anonymous viewers only convey their units via this header — see
      // useLocationForecast for the rationale. silent: the popup owns its
      // inline error state; an out-of-domain click (regional model, panned
      // beyond the overlay) must not also raise the global error snackbar.
      {
        headers: {'X-SmartMixin-Units': unitsHeader()},
        silent: true,
      },
    )
      .then((forecast: WeatherForecast) => {
        if (!cancelled) {
          setResult({key, status: 'ready', forecast});
        }
      })
      .catch(() => {
        if (!cancelled) {
          setResult({key, status: 'error', forecast: null});
        }
      });
    // Drop late responses when the point changes or on unmount.
    return () => {
      cancelled = true;
    };
  }, [fetch, key, lat, lon, modelName, meshSize]);

  // Derived: no point → idle; result not landed for the current key → loading.
  const current = result.key === key && key !== null;
  const status: ForecastMapPointValueStatus =
    key === null ? 'idle' : !current ? 'loading' : result.status;

  const value = React.useMemo(() => {
    if (!current || result.forecast === null) {
      return null;
    }
    // The frame's `time` is epoch SECONDS over the wire (DRF
    // DATETIME_FORMAT="%s") — `Date.parse` on such a string is NaN, which
    // made the popup answer "no data" for every click. Accept ISO too so a
    // differently-serialized fixture or endpoint keeps matching.
    const parsedSeconds = Number(frameTime);
    const frameTs = Number.isNaN(parsedSeconds)
      ? Date.parse(frameTime) / 1000
      : parsedSeconds;
    if (Number.isNaN(frameTs)) {
      return null;
    }
    const hourly = result.forecast.data?.hourly ?? [];
    let best: HourlyWeatherForecastItem | null = null;
    let bestDistance = Infinity;
    for (const item of hourly) {
      const distance = Math.abs(item.timestamp - frameTs);
      if (distance < bestDistance) {
        best = item;
        bestDistance = distance;
      }
    }
    if (best === null || bestDistance > MAX_MATCH_DISTANCE_S) {
      return null;
    }
    const raw = best[measure];
    return typeof raw === 'number' ? raw : null;
  }, [current, result.forecast, frameTime, measure]);

  const unit = Units.getUnitLabel(units, measure);

  return React.useMemo(() => ({status, value, unit}), [status, value, unit]);
};

export default useForecastMapPointValue;
